import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState, type MouseEvent } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { useMouseContent } from 'src/utils/mouse/mouseContent';
import { formatDate } from 'src/utils/string/stringUtil';

import {
	defaultTQC3020_R01ProductBaseDto,
	defaultTQC3020_R01ProductLotDto,
	TQC3020_R01ProductBaseDto,
	TQC3020_R01ProductLotDto,
} from '../Dto_TQC3020_R01';
import { useTQC3020_R01Store } from '../Store_TQC3020_R01';

const PRODUCT_COLUMN_STATE_KEY = 'TQC3020_R01-product-grid-column-state';
const PRODUCT_LOT_COLUMN_STATE_KEY = 'TQC3020_R01-product-lot-grid-column-state';

const applyBottomCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) =>
				params?.node?.rowPinned === 'bottom' ? { textAlign: 'center' } : null,
		};
	});

interface Props {
	productGridRef: React.RefObject<AgGridReact<TQC3020_R01ProductBaseDto> | null>;
	productLotGridRef: React.RefObject<AgGridReact<TQC3020_R01ProductLotDto> | null>;
	onProductRowClick: (data: TQC3020_R01ProductBaseDto) => void;
	onProductLotRowClick: (data: TQC3020_R01ProductLotDto) => void;
}

export const TableProductPane_TQC3020_R01 = ({
	productGridRef,
	productLotGridRef,
	onProductRowClick,
	onProductLotRowClick,
}: Props) => {
	const {
		TQC3020_R01productBaseList,
		selectedTQC3020_R01ProductBase,
		setSelectedTQC3020_R01ProductBase,
		TQC3020_R01productLotList,
		selectedTQC3020_R01ProductLot,
		setSelectedTQC3020_R01ProductLot,
		TQC3020_R01productBaseColumnState,
		setTQC3020_R01ProductBaseColumnState,
		TQC3020_R01productLotColumnState,
		setTQC3020_R01ProductLotColumnState,
		TQC3020ScrollTop,
		setTQC3020ScrollTop,
	} = useTQC3020_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);
	const [, setHoveredRowIndexLot] = useState<number | null>(null);

	const RESIZER = {
		code: 'TQC3020_R01-main',
		type: 'width' as const,
		storageKey: 'TQC3020_R01-main-split-width',
		min: 20,
		max: 80,
		default: 50,
	} as const;

	const containerRef = useRef<HTMLDivElement>(null);
	const lotTableRef = useRef<HTMLDivElement>(null);
	const isResizing = useRef(false);

	const { splitValue, handleMouseDown } = useMouseContent({
		resizer: RESIZER,
		isResizing,
		containerRef,
		infoRef: lotTableRef,
	});

	const {
		getRowId: getProductRowId,
		settingRowIndex: productSettingRowIndex,
		createRowClassRules: createProductRowClassRules,
		paddedGridData: productPaddedGridData,
	} = useGridSettingContent({
		gridRef: productGridRef,
		key: 'valueIndex',
		gridData: TQC3020_R01productBaseList,
		defaultGridData: {} as TQC3020_R01ProductBaseDto,
		selectedRow: selectedTQC3020_R01ProductBase,
	});

	const {
		getRowId: getProductLotRowId,
		settingRowIndex: productLotSettingRowIndex,
		createRowClassRules: createProductLotRowClassRules,
		paddedGridData: productLotPaddedGridData,
	} = useGridSettingContent({
		gridRef: productLotGridRef,
		key: 'valueIndex',
		gridData: TQC3020_R01productLotList,
		defaultGridData: {} as TQC3020_R01ProductLotDto,
		selectedRow: undefined,
	});

	const productBaseColumnDefs: ColDef<TQC3020_R01ProductBaseDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '생산실적번호', colId: 'productResultNo', field: 'productResultNo', width: 130,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 80,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '생산일자', colId: 'productDt', field: 'productDt', width: 100,
			valueFormatter: (p) => {
				if (!p.value) return '';
				try {
					return formatDate(new Date(p.value));
				} catch {
					return p.value;
				}
			},
		},
		{
			headerName: '작업장', colId: 'workplaceNm', field: 'workplaceNm', width: 100,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '모델명', colId: 'modelNo', field: 'modelNo', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '합격수량', colId: 'acceptQty', field: 'acceptQty', width: 80,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
	], []);

	const productDefaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(productBaseColumnDefs, [])
	, [productBaseColumnDefs]);

	const productColumnDefs = useMemo(() => {
		const merged = TQC3020_R01productBaseColumnState === null
			? productBaseColumnDefs
			: GridCustomColumnDefs<TQC3020_R01ProductBaseDto>({
				baseColumnDefs: productBaseColumnDefs,
				columnState: TQC3020_R01productBaseColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyBottomCenterAlign(merged, new Set(['productResultNo']));
	}, [TQC3020_R01productBaseColumnState, productSettingRowIndex, productBaseColumnDefs]);

	const productLotBaseColumnDefs: ColDef<TQC3020_R01ProductLotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: 'Lot No', colId: 'lotNo', field: 'lotNo', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '수량', colId: 'lotQty', field: 'lotQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
		{
			headerName: '단위', colId: 'unit', field: 'unit', width: 80,
			headerClass: aggridHeaderClass,
		},
	], []);

	const productLotDefaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(productLotBaseColumnDefs, [])
	, [productLotBaseColumnDefs]);

	const productLotColumnDefs = useMemo(() =>
		TQC3020_R01productLotColumnState === null
			? productLotBaseColumnDefs
			: GridCustomColumnDefs<TQC3020_R01ProductLotDto>({
				baseColumnDefs: productLotBaseColumnDefs,
				columnState: TQC3020_R01productLotColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			})
	, [TQC3020_R01productLotColumnState, productLotSettingRowIndex, productLotBaseColumnDefs]);

	const handleProductRowClicked = useCallback((e: RowClickedEvent<TQC3020_R01ProductBaseDto>) => {
		if (e.node.rowPinned === 'bottom' || !e.data) return;
		setSelectedTQC3020_R01ProductBase(e.data);
		onProductRowClick(e.data);
	}, [setSelectedTQC3020_R01ProductBase, onProductRowClick]);

	const handleProductLotRowClicked = useCallback((e: RowClickedEvent<TQC3020_R01ProductLotDto>) => {
		if (e.node.rowPinned === 'bottom' || !e.data) return;
		setSelectedTQC3020_R01ProductLot(e.data);
		onProductLotRowClick(e.data);
	}, [setSelectedTQC3020_R01ProductLot, onProductLotRowClick]);

	const productPinnedBottomRow = useMemo(() => ([{
		productResultNo: `${TQC3020_R01productBaseList.length.toLocaleString()}건`,
		acceptQty: TQC3020_R01productBaseList.reduce((sum, item) => sum + (item.acceptQty || 0), 0).toLocaleString(),
	}]), [TQC3020_R01productBaseList]);

	return (
		<div className="flex-1 flex gap-[4px]" ref={containerRef}>
			<div className="h-full" style={{ width: `${splitValue}%` }}>
				<GridContextWrapper<TQC3020_R01ProductBaseDto>
					enableCellCalculation
					gridRef={productGridRef}
					paddedGridData={productPaddedGridData}
					initSelected={() => setSelectedTQC3020_R01ProductBase(undefined)}
					defaultRowData={defaultTQC3020_R01ProductBaseDto}
					baseColumnDefs={TQC3020_R01productBaseColumnState ? productColumnDefs : productBaseColumnDefs}
					defaultColumnState={productDefaultColumnState}
					setColumnState={setTQC3020_R01ProductBaseColumnState}
					localStorageKey={PRODUCT_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<TQC3020_R01ProductBaseDto>
						ref={productGridRef}
						rowData={productPaddedGridData}
						columnDefs={productColumnDefs}
						defaultColDef={defaultColDef}
						getRowId={getProductRowId}
						rowClassRules={createProductRowClassRules<TQC3020_R01ProductBaseDto>({
							idKey: 'valueIndex',
							selectedList: selectedTQC3020_R01ProductBase?.valueIndex ? [selectedTQC3020_R01ProductBase.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleProductRowClicked}
						onCellKeyDown={undefined}
						pinnedBottomRowData={productPinnedBottomRow}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={PRODUCT_COLUMN_STATE_KEY}
						setColumnState={setTQC3020_R01ProductBaseColumnState}
						scrollTop={TQC3020ScrollTop.product}
						setScrollTop={(top) => setTQC3020ScrollTop('product', top)}
					/>
				</GridContextWrapper>
			</div>

			<div
				className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
				onMouseDown={(e: MouseEvent<HTMLDivElement>) => handleMouseDown('LR', e.nativeEvent)}
				data-comment="리사이저1"
			/>

			<div className="h-full" style={{ width: `${100 - splitValue}%` }} ref={lotTableRef}>
				<GridContextWrapper<TQC3020_R01ProductLotDto>
					enableCellCalculation
					gridRef={productLotGridRef}
					paddedGridData={productLotPaddedGridData}
					initSelected={() => setSelectedTQC3020_R01ProductLot(undefined)}
					defaultRowData={defaultTQC3020_R01ProductLotDto}
					baseColumnDefs={TQC3020_R01productLotColumnState ? productLotColumnDefs : productLotBaseColumnDefs}
					defaultColumnState={productLotDefaultColumnState}
					setColumnState={setTQC3020_R01ProductLotColumnState}
					localStorageKey={PRODUCT_LOT_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<TQC3020_R01ProductLotDto>
						ref={productLotGridRef}
						rowData={productLotPaddedGridData}
						columnDefs={productLotColumnDefs}
						defaultColDef={defaultColDef}
						getRowId={getProductLotRowId}
						rowClassRules={createProductLotRowClassRules<TQC3020_R01ProductLotDto>({
							idKey: 'valueIndex',
							selectedList: selectedTQC3020_R01ProductLot?.valueIndex ? [selectedTQC3020_R01ProductLot.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleProductLotRowClicked}
						onCellKeyDown={undefined}
						setHoveredRowIndex={setHoveredRowIndexLot}
						columnStateKey={PRODUCT_LOT_COLUMN_STATE_KEY}
						setColumnState={setTQC3020_R01ProductLotColumnState}
						scrollTop={TQC3020ScrollTop.productLot}
						setScrollTop={(top) => setTQC3020ScrollTop('productLot', top)}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
